import { create } from 'zustand';
import { taxConfigAPI, type TaxConfigDto } from '@matix/api-client';

/**
 * Tenant tax-config store.
 *
 * Loaded once on cashier app boot via `loadTaxConfig()`. The cart, the
 * cart panel, and the discount modal read from here instead of a
 * hardcoded constant, so the per-subsidiary VAT rate on Matix is what
 * the till uses.
 *
 * Fallback while loading: `sales_percentage = 0` — cart math still works,
 * VAT line shows 0 until the API call resolves.
 */

interface TaxConfigState {
  config: TaxConfigDto;
  loaded: boolean;
  /** Tax rate as a fraction (0.15 for 15%). Derived helper for math. */
  rate: () => number;
  setConfig: (c: TaxConfigDto) => void;
}

const DEFAULT: TaxConfigDto = {
  sales_percentage: 0,
  price_inclusive: false,
  label_en: 'VAT',
  label_ar: 'ضريبة',
};

export const useTaxConfig = create<TaxConfigState>((set, get) => ({
  config: DEFAULT,
  loaded: false,
  rate: () => get().config.sales_percentage / 100,
  setConfig: (config) => set({ config, loaded: true }),
}));

/**
 * Fetch + cache tenant tax config. Safe to call multiple times — the API
 * call is cheap and idempotent. Errors are swallowed so a misconfigured
 * tax table doesn't crash the till; the cart just shows 0% VAT until
 * fixed.
 */
export async function loadTaxConfig(): Promise<void> {
  try {
    const c = await taxConfigAPI.get();
    useTaxConfig.getState().setConfig(c);
  } catch (err) {
    // eslint-disable-next-line no-console
    console.warn('[tax-config] failed to load, using zero-VAT fallback', err);
  }
}
